import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import MuiButton from "@components/Button/MuiButton";
import {
  Box,
  Divider,
  Typography,
  Drawer,
  TextField,
  Avatar,
  IconButton,
  LinearProgress,
  Card,
  CardMedia,
  CardContent,
  Alert,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { CloudDownload, RemoveRedEye } from "@mui/icons-material";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@store/store";
import AttachFileOutlinedIcon from "@mui/icons-material/AttachFileOutlined";
import {
  fetchSeekClarificationDetailsApi,
  SubmitSeekClarificationDetailsApi,
} from "@modules/application-section/application-details/redux/services/thunk/DetailsThunk";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { toast } from "react-toastify";
import {
  getFileSignedURLMorthThunk as getMorthAccessSignedURLThunk,
  getFileSignedURLThunk as getFileSignedURLAccessThunk,
} from "@modules/stepper/redux/thunk/documentThunk";
import {
  getFileSignedURLMorthThunk as getMorthRowSignedURLThunk,
  getFileSignedURLThunk as getFileSignedURLRowThunk,
} from "@modules/stepper-row/redux/thunk/documentThunk";
import { uploadToS3Thunk } from "@store/thunk/UploadS3Thunk";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ClearIcon from "@mui/icons-material/Clear";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { fetchApplicationDetails } from "@modules/provisional-payment/redux/thunk/application-detailThunk";
import {
  getSignedPreviewUrl,
  openFileBySignedUrl,
} from "../../utils/getViewSignedUrlHandler";
import { genericUploadThunk } from "@modules/provisional-payment/redux/thunk/uploadThunk";
import { productionCheck } from "@components/buttonGroupView/utils";

// Add FilePreviewModal Component
const FilePreviewModal = ({
  open,
  onClose,
  file,
  fileType,
  previewDocUrl,
}: {
  open: boolean;
  onClose: () => void;
  file: any;
  fileType: "image" | "video" | "pdf" | "other";
  previewDocUrl: any;
}) => {
  const getPreviewContent = () => {
    switch (fileType) {
      case "image":
        return (
          <CardMedia
            component="img"
            image={previewDocUrl}
            alt={file.fileName}
            sx={{
              maxHeight: { xs: "400px", sm: "500px", md: "700px" },
              width: "100%",
              objectFit: "contain",
            }}
          />
        );
      case "video":
        return (
          <Box display="flex" justifyContent="center">
            <CardMedia
              component="video"
              src={previewDocUrl}
              controls
              autoPlay
              sx={{
                maxHeight: { xs: "300px", sm: "500px", md: "700px" },
                width: "100%",
                objectFit: "contain",
                borderRadius: 1,
              }}
            />
          </Box>
        );

      case "pdf":
        return (
          <Box
            sx={{
              width: "100%",
              height: { xs: "400px", sm: "500px", md: "600px" },
              overflow: "auto",
            }}
          >
            <iframe
              src={previewDocUrl}
              style={{
                width: "100%",
                height: "100%",
                border: "none",
              }}
              title={file.fileName}
            />
          </Box>
        );
      default:
        return (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography>Preview not available for this file type</Typography>
          </Box>
        );
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        "& .MuiDrawer-paper": {
          width: { xs: "100vw", sm: "70%" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
        },
      }}
    >
      <Box sx={{ p: 3, width: "100%", overflowX: "hidden" }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">{file.fileName}</Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        {getPreviewContent()}
      </Box>
    </Drawer>
  );
};

dayjs.extend(utc);

type Props = {
  open: boolean;
  onClose: () => void;
  access_id: string | number;
  onSuccess: () => void;
  isROW?: boolean;
};

interface SeekClarificationDetails {
  application_id: number;
  documents: any[];
  id: number;
  message: string;
  message_type: string;
  recipient_id: number;
  date: string;
  time: string;
  sender_details: {
    first_name: string;
    last_name: string;
    middle_name: string;
    roles: any[];
    user_id: number;
  };
  sender_id: number;
}

interface DocumentData {
  fileName: string;
  objectKey: string;
  // documentKey: string;
  fileType: "pdf" | "image" | "video" | "other";
  previewUrl?: string;
  uploadProgress?: number;
  isUploading?: boolean;
}

const SeekClarificationModel: React.FC<Props> = ({
  open,
  onClose,
  access_id,
  onSuccess,
  isROW,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [remarks, setRemarks] = useState("");
  const [data, setData] = useState<SeekClarificationDetails[]>([]);
  const [uploadedDocuments, setUploadedDocuments] = useState<DocumentData[]>(
    [],
  );
  const [isDragging, setIsDragging] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<DocumentData | null>(null);
  const [previewDocUrl, setPreviewDocUrl] = useState<string>();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const { profile } = useSelector((state: RootState) => state.profile);

  const generateSignedUrl = async (doc_name: string) => {
    try {
      const signedUrl = await getSignedPreviewUrl({
        document_name: doc_name,
        dispatch,
        getSignedUrlThunk: isROW
          ? getFileSignedURLRowThunk
          : getFileSignedURLAccessThunk,
      });

      if (!signedUrl) return;

      const response = await fetch(signedUrl, {
        credentials: "include",
      });

      const blob = await response.blob();

      const blobUrl = URL.createObjectURL(blob);

      setPreviewDocUrl(blobUrl);

      // if (doc_name.match(/\.(jpg|jpeg|png|webp)$/i)) {
      //   setFileType("image");
      // } else if (doc_name.match(/\.pdf$/i)) {
      //   setFileType("pdf");
      // } else {
      //   setFileType("other");
      // }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    return () => {
      if (previewDocUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previewDocUrl);
      }
    };
  }, [previewDocUrl]);

  const handleCloseSlider = () => {
    setUploadedDocuments([]); // Clear uploaded files when closing
    setRemarks("");
    onClose();
  };

  const getObjectKey = (path?: string) => {
    if (!path) return undefined;
    return path.replace(/^(noc-document|morth-documents)\/(row|access)\//, "");
  };

  // Handle file upload
  const handleFileUpload = async (file: File): Promise<boolean> => {
    if (file.size > 21 * 1024 * 1024) {
      toast.error("File size must be less than 20 MB");
      return false;
    }

    const tempObjectKey = `temp-seek-clarification-${Date.now()}-${file.name}`;
    const previewUrl = URL.createObjectURL(file);

    // Determine file type
    const fileType = file.type.startsWith("image/")
      ? "image"
      : file.type === "application/pdf"
        ? "pdf"
        : file.type.startsWith("video/")
          ? "video"
          : "other";

    const tempDocument: DocumentData = {
      fileName: file.name,
      objectKey: tempObjectKey,
      fileType,
      previewUrl,
      isUploading: true,
      uploadProgress: 0,
    };

    setUploadedDocuments((prev) => [...prev, tempDocument]);

    try {
      if (file.size > 21 * 1024 * 1024) {
        toast.error("File size must be less than 20 MB");
        setUploadedDocuments((prev) =>
          prev.filter((doc) => doc.objectKey !== tempObjectKey),
        );
        return false;
      }
      // Get signed URL from backend
      const signedURLResponse = await dispatch(
        genericUploadThunk({
          file,
          moduleType: isROW ? "row" : "access",
        }),
      );

      if (!signedURLResponse?.payload?.success) {
        const details =
          signedURLResponse?.payload?.details ||
          signedURLResponse?.payload?.response?.data?.details;
        toast.error(
          Array.isArray(details)
            ? details[0]
            : typeof details === "string"
              ? details
              : "Something Went Wrong!",
        );
        setUploadedDocuments((prev) =>
          prev.filter((doc) => doc.objectKey !== tempObjectKey),
        );
        return false;
      }

      const { data } = signedURLResponse.payload;

      // Update progress
      setUploadedDocuments((prev) =>
        prev.map((doc) =>
          doc.objectKey === tempObjectKey
            ? { ...doc, uploadProgress: 50 }
            : doc,
        ),
      );
      if (!productionCheck) {
        // Upload to S3
        const uploadSuccess = await dispatch(
          uploadToS3Thunk({
            signedUrl: data.signedUrl,
            file,
            contentType: file.type,
          }),
        ).unwrap();

        if (!uploadSuccess) throw new Error("S3 upload failed");
      }
      // Create final document object
      const finalDocument: DocumentData = {
        fileName: data.originalFileName,
        objectKey: data.objectKey,
        fileType,
        previewUrl: previewUrl,
      };

      // Replace temp document with final one
      setUploadedDocuments((prev) =>
        prev.map((doc) =>
          doc.objectKey === tempObjectKey ? finalDocument : doc,
        ),
      );

      return true;
    } catch (error) {
      console.error("Upload failed:", error);
      // Remove failed upload from state
      setUploadedDocuments((prev) =>
        prev.filter((doc) => doc.objectKey !== tempObjectKey),
      );
      // Revoke blob URL to prevent memory leak
      URL.revokeObjectURL(previewUrl);
      toast.error(`Failed to upload ${file.name}`);
      return false;
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    let allSuccess = true;
    for (const file of files) {
      const result = await handleFileUpload(file);
      if (result === false) allSuccess = false;
    }

    if (allSuccess && files.length > 1) {
      toast.success(`${files.length} files uploaded successfully`);
    }

    // Reset input
    event.target.value = "";
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;

    let allSuccess = true;
    for (const file of files) {
      const result = await handleFileUpload(file);
      if (result === false) allSuccess = false;
    }

    if (allSuccess && files.length > 1) {
      toast.success(`${files.length} files uploaded successfully`);
    }
  };

  const handleRemoveDocument = (objectKey: string) => {
    const docToRemove = uploadedDocuments.find(
      (doc) => doc.objectKey === objectKey,
    );
    if (docToRemove?.previewUrl) {
      URL.revokeObjectURL(docToRemove.previewUrl);
    }
    setUploadedDocuments((prev) =>
      prev.filter((doc) => doc.objectKey !== objectKey),
    );
  };

  const handlePreview = async (file: DocumentData) => {
    if (file.isUploading) return;
    setSelectedFile(file);
    setPreviewOpen(true);
    const objectKey = getObjectKey(file?.objectKey);

    if (!objectKey) return;

    const res = await generateSignedUrl(objectKey);
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setSelectedFile(null);
  };

  const renderFilePreview = (file: DocumentData) => {
    if (file.fileType === "image" || file.fileType === "video") {
      return (
        <Card
          sx={{
            maxWidth: { xs: "80px", sm: "100px" },
            m: 0.5,
            position: "relative",
          }}
        >
          {file.isUploading && (
            <LinearProgress
              variant="determinate"
              value={file.uploadProgress || 0}
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                zIndex: 2,
              }}
            />
          )}

          <Box display="flex" position="relative">
            {/* Thumbnail */}
            {file.fileType === "image" ? (
              <CardMedia
                component="img"
                
                image={file.previewUrl}
                alt={file.fileName}
                sx={{
                  objectFit: "cover",
                  cursor: file.isUploading ? "not-allowed" : "pointer",
                  opacity: file.isUploading ? 0.7 : 1,
                  width: "100%",
                  height: { xs: "70px", sm: "80px" },
                }}
                onClick={() => !file.isUploading && handlePreview(file)}
              />
            ) : (
              <CardMedia
                component="video"
                src={file.previewUrl}
                sx={{
                  objectFit: "cover",
                  cursor: file.isUploading ? "not-allowed" : "pointer",
                  opacity: file.isUploading ? 0.7 : 1,
                  width: "100%",
                  height: { xs: "70px", sm: "80px" },
                }}
              />
            )}

            {/* Play Icon Overlay (Only for Video) */}
            {file.fileType === "video" && (
              <Box
                onClick={() => !file.isUploading && handlePreview(file)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  color: "#fff",
                  backgroundColor: "rgba(0,0,0,0.5)",
                  borderRadius: "50%",
                  p: 0.5,
                  cursor: file.isUploading ? "not-allowed" : "pointer",
                }}
              >
                <PlayCircleOutlineIcon fontSize="large" />
              </Box>
            )}
          </Box>

          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <Typography variant="caption" noWrap sx={{ fontSize: { xs: 10, sm: 12 } }}>
              {file.fileName}
              {file.isUploading && ` (${file.uploadProgress}%)`}
            </Typography>
          </CardContent>
        </Card>
      );
    } else if (file.fileType === "pdf") {
      return (
        <Card
          sx={{
            maxWidth: { xs: "100px", sm: "150px" },
            m: 0.5,
            position: "relative",
          }}
        >
          {file.isUploading && (
            <LinearProgress
              variant="determinate"
              value={file.uploadProgress || 0}
              sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
            />
          )}
          <CardContent
            sx={{ p: 1, cursor: file.isUploading ? "not-allowed" : "pointer" }}
            onClick={() => !file.isUploading && handlePreview(file)}
          >
            <PictureAsPdfIcon sx={{ fontSize: { xs: 20, sm: 25 }, color: "#E53935" }} />
            <Typography
              variant="caption"
              display="block"
              noWrap
              sx={{ fontSize: { xs: 10, sm: 12 } }}
            >
              {file.fileName}
              {file.isUploading && ` (${file.uploadProgress}%)`}
            </Typography>
          </CardContent>
        </Card>
      );
    } else {
      return (
        <Box
          sx={{
            border: "1px solid #ddd",
            borderRadius: 1,
            p: 1,
            m: 0.5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            position: "relative",
            fontSize: { xs: 12, sm: 14 },
          }}
        >
          {file.isUploading && (
            <LinearProgress
              variant="determinate"
              value={file.uploadProgress || 0}
              sx={{ position: "absolute", top: 0, left: 0, right: 0 }}
            />
          )}
          <AttachFileOutlinedIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
          <Typography
            variant="caption"
            noWrap
            sx={{ maxWidth: { xs: "70px", sm: "100px" }, fontSize: { xs: 10, sm: 12 } }}
          >
            {file.fileName}
            {file.isUploading && ` (${file.uploadProgress}%)`}
          </Typography>
        </Box>
      );
    }
  };

  const submitRecommedAction = async () => {
    // Check if any files are still uploading
    const uploadingFiles = uploadedDocuments.filter((doc) => doc.isUploading);
    if (uploadingFiles.length > 0) {
      toast.error("Please wait for all files to finish uploading");
      return;
    }

    // Validate remarks
    if (!remarks.trim()) {
      toast.error("Please enter remarks");
      return;
    }

    // Prepare documents payload
    const documentsPayload = uploadedDocuments
      .filter((doc) => !doc.isUploading)
      .map((doc) => ({
        fileName: doc.fileName,
        objectKey: doc.objectKey,
      }));

    const commonPayload = {
      application_id: access_id,
      remarks: remarks,
      action_type: "CLARIFICATION_RESPONSE",
      documents: documentsPayload,
    };

    try {
      const res = await dispatch(
        SubmitSeekClarificationDetailsApi(commonPayload),
      );
      if (res.payload?.success) {
        toast.success(res.payload?.message);
        // Clear state
        uploadedDocuments.forEach((doc) => {
          if (doc.previewUrl) URL.revokeObjectURL(doc.previewUrl);
        });
        setUploadedDocuments([]);
        setRemarks("");
        fetchClarificationDetails(access_id); // Refresh data
        onSuccess?.();
        onClose();
      } else {
        toast.error(res.payload?.message || "Submission failed");
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast.error("Submission failed");
    }
  };

  const fetchClarificationDetails = async (id: any) => {
    try {
      const res = await dispatch(fetchSeekClarificationDetailsApi(id));

      setData(res?.payload?.data || []);
    } catch (error) {
      console.error("Error fetching seek clarification details:", error);
    }
  };

  useEffect(() => {
    if (access_id && open) {
      fetchClarificationDetails(access_id);
    }
  }, [access_id, open]);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      uploadedDocuments.forEach((doc) => {
        if (doc.previewUrl) URL.revokeObjectURL(doc.previewUrl);
      });
    };
  }, []);

  const handleBoxClick = (event: React.MouseEvent<HTMLDivElement>) => {
    fileInputRef.current?.click();
  };

  // const openFile = (path?: string) => {
  //   if (path) {
  //     window.open(
  //       process.env.REACT_APP_API_IMAGE_URL + path,
  //       "_blank",
  //       "noopener,noreferrer",
  //     );
  //   } else {
  //     alert("View URL not found.");
  //   }
  // };

  const handleViewFile = (fullPath: string) => {
    if (!fullPath) return;

    const isMorth = fullPath.startsWith("morth-documents/");
    const isRow = fullPath.includes("/row/");
    const isAccess = fullPath.includes("/access/");

    // Remove prefix
    const objectKey = fullPath.replace(
      /^(noc-document|morth-documents)\/(row|access)\//,
      "",
    );

    // Decide correct thunk
    let selectedThunk;

    if (isMorth) {
      selectedThunk = isRow
        ? getMorthRowSignedURLThunk
        : getMorthAccessSignedURLThunk;
    } else {
      selectedThunk = isRow
        ? getFileSignedURLRowThunk
        : getFileSignedURLAccessThunk; // replace if access has separate thunk
    }

    openFileBySignedUrl({
      document_name: objectKey,
      dispatch,
      getSignedUrlThunk: selectedThunk,
    });
  };

  const sanitizeRemarks = (value: string) => {
    if (!value) return value;

    return (
      value
        // Remove <script>...</script> blocks
        .replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gi, "")
        // Remove all other HTML tags
        .replace(/<\/?[^>]+(>|$)/g, "")
      // Optionally decode HTML entities like &nbsp; or &amp;
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleCloseSlider}
        sx={{
          "& .MuiDrawer-paper": {
            width: { xs: "100vw", sm: "500px" },
            maxWidth: "100%",
            boxSizing: "border-box",
            overflowX: "hidden",
          },
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <Box sx={{ px: { xs: 2, sm: 4 }, py: 2, width: "100%", boxSizing: "border-box" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderColor: "divider",
              }}
            >
              <Typography
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontWeight: 700,
                  fontSize: { xs: "16px", sm: "18px" },
                  color: "#212121",
                  lineHeight: "24px",
                }}
              >
                Seek Clarifications
              </Typography>
              <CloseIcon
                onClick={handleCloseSlider}
                sx={{ cursor: "pointer", "&:hover": { color: "primary.main" } }}
              />
            </Box>
          </Box>
          <Divider />

          <Box
            px={{ xs: 1, sm: 2 }}
            style={{ flex: 1, display: "flex", flexDirection: "column", width: "100%", overflow: "hidden", boxSizing: "border-box" }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                px: { xs: 0.5, sm: 1 },
                py: 2,
                overflowY: "auto",
                overflowX: "hidden",
                width: "100%",
              }}
            >
              {/* Authority Message list */}
              {data?.map((item) => (
                <Box
                  key={item?.id}
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  width="100%"
                  sx={{ minWidth: 0 }}
                >
                  <Avatar sx={{ width: 36, height: 36, flexShrink: 0 }}>
                    {`${item?.sender_details?.first_name?.charAt(0) || ""}${
                      item?.sender_details?.last_name?.charAt(0) || ""
                    }`}
                  </Avatar>
                  <Box width="100%" sx={{ minWidth: 0 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      sx={{ flexWrap: "wrap", gap: 1 }}
                    >
                      <Box>
                        <Typography
                          variant="subtitle2"
                          fontWeight="bold"
                          sx={{ fontWeight: 500, fontSize: { xs: 14, sm: 16 } }}
                        >
                          {item?.sender_details?.first_name}{" "}
                          {item?.sender_details?.last_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 300, fontSize: { xs: 10, sm: 11 } }}
                        >
                          {item?.sender_details?.roles[0]?.role_name}
                        </Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 500, fontSize: { xs: 12, sm: 14 } }}
                        >
                          {item?.date
                            ? dayjs(item.date).format("DD/MM/YYYY")
                            : ""}
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#fff",
                        border: "1px solid #e0e0e0",
                        borderRadius: 2,
                        fontWeight: 300,
                        fontSize: { xs: 12, sm: 14 },
                        p: { xs: 1, sm: 1.5 },
                        mt: 1,
                        maxWidth: "100%",
                        width: "100%",
                      }}
                    >
                      <Typography variant="body2">{item?.message}</Typography>
                    </Box>

                    {/* Show attached documents if they exist */}
                    {item?.documents && item.documents.length > 0 && (
                      <Box mt={1}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontWeight: 500, mb: 0.5, display: "block" }}
                        >
                          Attached files:
                        </Typography>
                        {item.documents.map((doc, index) => (
                          <Box
                            key={doc.document_id || index}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{
                              border: "1px solid #ccc",
                              borderRadius: 1,
                              px: { xs: 1, sm: 2 },
                              py: 1,
                              backgroundColor: "#fff",
                              mb: 1,
                              maxWidth: "100%",
                              width: "100%",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} flex={1} minWidth={0}>
                              <Avatar
                                sx={{
                                  bgcolor: doc.fileName?.endsWith(".pdf")
                                    ? "#d32f2f"
                                    : "#1976d2",
                                  width: 28,
                                  height: 28,
                                  fontSize: 14,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                {doc.fileName?.endsWith(".pdf") ? (
                                  <PictureAsPdfIcon
                                    sx={{ fontSize: 16, color: "#fff" }}
                                  />
                                ) : (
                                  <AttachFileOutlinedIcon
                                    sx={{ fontSize: 16, color: "#fff" }}
                                  />
                                )}
                              </Avatar>
                              <Box minWidth={0}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 400, fontSize: { xs: 11, sm: 12 } }}
                                  noWrap
                                >
                                  {doc.fileName || doc.original_file_name}
                                </Typography>
                                {doc.file_size > 0 && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontSize: { xs: 10, sm: 11 } }}
                                  >
                                    {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                const objectKey =
                                  doc.objectKey || doc.object_key;

                                handleViewFile(objectKey);
                              }}
                              sx={{
                                "&:hover": {
                                  backgroundColor: "#E7E4F8",
                                  color: "#3A1C73",
                                },
                                flexShrink: 0,
                              }}
                            >
                              <RemoveRedEye
                                fontSize="small"
                                sx={{ color: "#757575" }}
                              />
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </Box>
              ))}

              {/* Upload Status Alerts */}
              {uploadedDocuments.some((doc) => doc.isUploading) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Uploading files... Please wait.
                </Alert>
              )}

              {/* {uploadedDocuments.filter(doc => !doc.isUploading).length > 0 && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {uploadedDocuments.filter(doc => !doc.isUploading).length} file(s) uploaded successfully
                </Alert>
              )} */}

              {/* Response Section */}
              <Box width="100%" sx={{ boxSizing: "border-box" }}>
                <Box
                  display="flex"
                  alignItems="flex-start"
                  gap={1}
                  width="100%"
                  sx={{ minWidth: 0, boxSizing: "border-box" }}
                >
                  <Avatar sx={{ width: 36, height: 36, bgcolor: "#3A1C73", flexShrink: 0 }}>
                    {profile?.name?.[0]?.toUpperCase() || ""}
                  </Avatar>
                  <Box width="100%" sx={{ minWidth: 0 }}>
                    <TextField
                      label="Remarks"
                      required
                      multiline
                      rows={4}
                      fullWidth
                      value={remarks}
                      onChange={(e) => {
                        if (e.target.value.length <= 200) {
                          setRemarks(sanitizeRemarks(e.target.value));
                        }
                      }}
                      InputLabelProps={{
                        sx: {
                          fontWeight: 300,
                          fontSize: { xs: 12, sm: 14 },
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        textAlign: "right",
                        mt: 0.5,
                        fontSize: { xs: 11, sm: 12 },
                      }}
                    >
                      {remarks.length}/200
                    </Typography>

                    {/* Upload Area */}
                    <Box
                      ref={dropZoneRef}
                      onClick={handleBoxClick}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      sx={{
                        mt: 2,
                        width: "100%",
                        boxSizing: "border-box",
                        border: `1.5px dashed ${
                          isDragging ? "#3A1C73" : "#C7C7C7"
                        }`,
                        borderRadius: 2,
                        p: { xs: 1.5, sm: 2 },
                        textAlign: "center",
                        bgcolor: isDragging ? "#F0F0F0" : "#FAFAFA",
                        cursor: "pointer",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: "#3A1C73",
                          bgcolor: "#F5F5F5",
                        },
                      }}
                    >
                      <CloudUploadOutlinedIcon
                        sx={{
                          color: "#3A1C73",
                          mb: 1,
                          width: { xs: 24, sm: 30 },
                          height: { xs: 18, sm: 22 },
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: "Inter, sans-serif",
                          fontWeight: 500,
                          fontSize: { xs: "12px", sm: "14px" },
                          lineHeight: "20px",
                        }}
                      >
                        Drag & Drop Files or{" "}
                        <span
                          style={{
                            color: "#3A1C73",
                            cursor: "pointer",
                            textDecoration: "underline",
                          }}
                        >
                          Browse Files
                        </span>
                        <br />
                        <span style={{ fontSize: "12px", color: "#757575" }}>
                          Please upload up to 20 MB per file. Supports multiple
                          files.
                        </span>
                      </Typography>

                      <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        accept="application/pdf, image/*, video/*"
                        multiple
                      />
                    </Box>

                    {/* Uploaded Files Preview */}
                    {uploadedDocuments.length > 0 && (
                      <Box mt={2} sx={{ width: "100%", boxSizing: "border-box" }}>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          mb={1}
                          sx={{ fontSize: { xs: 12, sm: 14 } }}
                        >
                          Uploaded files (
                          {
                            uploadedDocuments.filter((doc) => !doc.isUploading)
                              .length
                          }{" "}
                          ready):
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, width: "100%" }}>
                          {uploadedDocuments.map((doc) => (
                            <Box
                              key={doc.objectKey}
                              sx={{ position: "relative" }}
                            >
                              {renderFilePreview(doc)}
                              {!doc.isUploading && (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    handleRemoveDocument(doc.objectKey)
                                  }
                                  sx={{
                                    position: "absolute",
                                    zIndex: 5,
                                    top: 4,
                                    right: 4,
                                    bgcolor: "#00000042",
                                    color: "white",
                                    width: { xs: 22, sm: 25 },
                                    borderRadius: "unset",
                                    height: { xs: 20, sm: 22 },
                                    "&:hover": { bgcolor: "error.dark" },
                                  }}
                                >
                                  <ClearIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />
                                </IconButton>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              px={{ xs: 2, sm: 4 }}
              py={3}
              sx={{ borderTop: "1px solid", borderColor: "divider", width: "100%", boxSizing: "border-box" }}
            >
              <MuiButton
                type="button"
                name="Send Response"
                onClick={submitRecommedAction}
                disabled={
                  !remarks.trim() ||
                  uploadedDocuments.some((doc) => doc.isUploading)
                }
                sx={{
                  width: "100%",
                  borderRadius: "999px",
                  backgroundColor: "#928F25",
                  color: "#fff",
                  py: 1.5,
                  "&:hover": { backgroundColor: "#b0ad5cff" },
                  "&:disabled": { backgroundColor: "#ccc" },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Drawer>

      {/* File Preview Modal */}
      {selectedFile && (
        <FilePreviewModal
          open={previewOpen}
          onClose={handleClosePreview}
          file={selectedFile}
          fileType={selectedFile.fileType}
          previewDocUrl={previewDocUrl}
        />
      )}
    </>
  );
};

export default SeekClarificationModel;
